import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Message, Model } from '@shared/types';
import TextAreaChat from '@/components/TextAreaChat';
import { AssistantMessage } from '@/components/chat/AssistantMessage';
import { UserMessage } from '@/components/chat/UserMessage';
import { useConversation } from '@/services/conversationService';
import { AssistantLoading } from '@/components/chat/AssistantLoading';
import { ChatTitle } from '@/components/chat/ChatTitle';
import { TreeNode } from '@shared/Tree';
import { PARAMETRIC_MODELS } from '@/lib/utils';
import {
  useIsLoading,
  useSendContentMutation,
} from '@/services/messageService';
import { useCredits } from '@/contexts/CreditsContext';
import { MessageSquare, PanelLeftClose } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ChatSectionProps {
  messages: TreeNode<Message>[];
  onClose?: () => void;
}

export function ChatSection({ messages, onClose }: ChatSectionProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { conversation } = useConversation();
  const [model, setModel] = useState<Model>(PARAMETRIC_MODELS[0].id);
  const isLoading = useIsLoading();
  const wasLoadingRef = useRef(false);
  const { mutate: sendMessage } = useSendContentMutation({ conversation });
  const { refreshCredits } = useCredits();

  // Refresh credits when generation completes (isLoading goes from true to false)
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      refreshCredits();
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, refreshCredits]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastAssistantMessage = [...messages].reverse().find((m) => m.role === 'assistant');
      if (lastAssistantMessage?.content?.model) {
        setModel(lastAssistantMessage.content.model);
      }
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [isLoading, scrollToBottom]);

  const lastMessage = useMemo(() => {
    if (conversation.current_message_leaf_id) {
      return messages.find(
        (msg) => msg.id === conversation.current_message_leaf_id,
      );
    }
    return messages[messages.length - 1];
  }, [messages, conversation.current_message_leaf_id]);

  const getCurrentVersion = useCallback(
    (index: number) => {
      return messages.slice(0, index + 1).filter((m) => m.role === 'assistant')
        .length;
    },
    [messages],
  );

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden border-r border-dc-border-secondary bg-dc-bg-primary">
      {/* Gradient background effect */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-dc-accent-primary/5 via-transparent to-dc-accent-secondary/5" />

      {/* Header */}
      <div className="relative z-10 flex w-full items-center justify-between border-b border-dc-border-secondary bg-dc-bg-secondary/50 px-4 py-3 backdrop-blur-sm">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
            <MessageSquare className="h-4 w-4 text-dc-accent-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <ChatTitle />
          </div>
        </div>
        {onClose && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 rounded-lg p-0 text-dc-text-tertiary transition-colors hover:bg-dc-bg-hover hover:text-dc-text-primary"
                  onClick={onClose}
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="border-dc-border-primary bg-dc-bg-elevated">
                <p className="text-dc-text-primary">Collapse chat</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Messages Area */}
      <ScrollArea
        className="relative z-10 w-full flex-1 px-4"
        ref={scrollAreaRef}
      >
        {/* Top fade gradient */}
        <div className="pointer-events-none sticky left-0 top-0 z-50 h-6 bg-gradient-to-b from-dc-bg-primary to-transparent" />

        <div className="mx-auto max-w-2xl space-y-6 pb-8 pt-2">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
                <MessageSquare className="h-8 w-8 text-dc-accent-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-dc-text-primary">Start your design</h3>
              <p className="text-sm text-dc-text-tertiary">Describe what you want to create</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={message.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {message.role === 'assistant' ? (
                  <AssistantMessage
                    message={message}
                    currentVersion={getCurrentVersion(index)}
                  />
                ) : (
                  <UserMessage message={message} isLoading={isLoading} />
                )}
              </div>
            ))
          )}
          {isLoading && lastMessage?.role !== 'assistant' && (
            <div className="animate-fade-in">
              <AssistantLoading />
            </div>
          )}
        </div>

        {/* Bottom fade for smooth scroll */}
        <div className="pointer-events-none sticky bottom-0 left-0 h-6 bg-gradient-to-t from-dc-bg-primary to-transparent" />
      </ScrollArea>

      {/* Input Area */}
      <div className="relative z-10 border-t border-dc-border-secondary bg-dc-bg-secondary/30 px-4 py-4 backdrop-blur-sm">
        <div className="mx-auto max-w-2xl">
          <TextAreaChat
            onSubmit={sendMessage}
            placeholder="Keep iterating with DaddyCad..."
            disabled={isLoading}
            model={model}
            setModel={setModel}
            conversation={conversation}
          />
        </div>
      </div>
    </div>
  );
}
